import type { TypeDefaults } from '@components/ui/cards/TypeDefaultsPreview';

import type {
  CleanupTypeVersion,
  GroupResolution,
  LooseEndDecision,
  LooseEndSet,
  SharedCatalogEntry,
  WizardData,
} from '../types';

import { personalKey } from './deriveWizardData';

/** Identity signature: two types with equal signatures are interchangeable. */
const defaultsSignature = (v: CleanupTypeVersion): string =>
  JSON.stringify(v.defaults);

/**
 * The projected shared catalog: the automatic shared types plus every
 * make_shared winner from the admin's resolutions (both lanes), under its
 * post-merge name. Shared source of truth for the loose-ends sweep and the
 * Confirm step's catalog preview.
 */
export const projectSharedCatalog = (
  data: WizardData,
  resolutions: Readonly<Record<string, GroupResolution>>
): Array<{ version: CleanupTypeVersion; name: string }> => {
  const projected: Array<{ version: CleanupTypeVersion; name: string }> =
    data.sharedTypes.map((version) => ({ version, name: version.name }));
  // A group's canonical can be an auto-promoted row that already sits in
  // sharedTypes — never count the same type twice.
  const seen = new Set(projected.map((e) => e.version.id));

  const collectWinners = (
    groups: WizardData['divergentGroups'],
    keyOf: (nameKey: string) => string
  ): void => {
    for (const group of groups) {
      const resolution = resolutions[keyOf(group.nameKey)];
      if (resolution?.kind !== 'make_shared') continue;
      const winner = group.versions.find((v) => v.id === resolution.winnerId);
      if (!winner || seen.has(winner.id)) continue;
      seen.add(winner.id);
      projected.push({
        version: winner,
        name: resolution.sharedName?.trim() || group.displayName,
      });
    }
  };
  collectWinners(data.divergentGroups, (k) => k);
  collectWinners(data.personalClusters, personalKey);
  return projected;
};

/**
 * The final shared catalog as the picker will show it: the projection with
 * archive-all'd canonicals removed and the loose-end set decisions applied
 * (merged losers collapse into the keeper under its kept name; archived
 * sets drop entirely). Sorted by usage — the picker's own order.
 */
export const computeSharedCatalog = (args: {
  /** Post-quick-wins wizard data (what the lanes and loose ends see). */
  data: WizardData;
  resolutions: Readonly<Record<string, GroupResolution>>;
  looseEndSets: LooseEndSet[];
  /** COMMITTED loose-end decisions (empty names already normalized). */
  looseEndDecisions: Readonly<Record<string, LooseEndDecision>>;
  /** Ids shared before the wizard ran — everything else is newly shared. */
  originallySharedIds: ReadonlySet<string>;
  /** Set-aside types the admin archived — drop them from the picker preview. */
  setAsideArchivedIds?: ReadonlySet<string>;
  /** Set-aside rename map (typeId → kept name) — the catalog shows the kept
   *  name so a collision the admin resolved by renaming stops showing as one. */
  setAsideRenames?: Readonly<Record<string, string>>;
}): SharedCatalogEntry[] => {
  const dropped = new Set<string>(args.setAsideArchivedIds ?? []);
  for (const group of args.data.divergentGroups) {
    if (args.resolutions[group.nameKey]?.kind === 'archive_all') {
      for (const v of group.versions) dropped.add(v.id);
    }
  }

  const renames = new Map<string, string>(
    Object.entries(args.setAsideRenames ?? {})
  );
  // Merge keepers carry the RESULT: siblings' usage is absorbed, and a
  // near-merge keeper adopts the reconciled (union) defaults — the catalog
  // must show the post-merge type, not the keeper's pre-merge row.
  const keeperOutcome = new Map<
    string,
    { combinedUsage: number; mergedDefaults?: TypeDefaults }
  >();
  for (const set of args.looseEndSets) {
    const decision = args.looseEndDecisions[set.key];
    if (!decision) continue;
    if (decision.archive) {
      for (const e of set.entries) dropped.add(e.version.id);
    } else if (decision.merge) {
      const keeper =
        set.entries.find((e) => e.name === decision.keepName)?.version ??
        set.entries[0].version;
      for (const e of set.entries) {
        if (e.version.id !== keeper.id) dropped.add(e.version.id);
      }
      renames.set(keeper.id, decision.keepName);
      keeperOutcome.set(keeper.id, {
        combinedUsage: set.entries.reduce(
          (sum, e) => sum + e.version.usage,
          0
        ),
        mergedDefaults: set.near?.reconciled,
      });
    }
  }

  return projectSharedCatalog(args.data, args.resolutions)
    .filter((e) => !dropped.has(e.version.id))
    .map((e) => {
      const name = renames.get(e.version.id) ?? e.name;
      const outcome = keeperOutcome.get(e.version.id);
      return {
        version: e.version,
        name,
        isNew: !args.originallySharedIds.has(e.version.id),
        renamedFrom:
          name.trim() !== e.version.name.trim() ? e.version.name : null,
        mergedDefaults: outcome?.mergedDefaults,
        combinedUsage: outcome?.combinedUsage,
      };
    })
    .sort(
      (a, b) =>
        (b.combinedUsage ?? b.version.usage) -
        (a.combinedUsage ?? a.version.usage)
    );
};

/**
 * The loose-ends sweep (GDN-2226 lane 2): after name-based merging, the
 * projected shared catalog can still contain differently-named types that
 * are interchangeable — same template AND identical defaults (name-drift
 * like "MED" / "MEDS" / "Follow Up" that name-key grouping can't see).
 * Types that share a template but DIFFER in defaults (duration variants
 * etc.) are legitimate and never flagged.
 */
export const computeLooseEndSets = (
  data: WizardData,
  resolutions: Readonly<Record<string, GroupResolution>>
): LooseEndSet[] => {
  const projected = projectSharedCatalog(data, resolutions);

  const byTemplate = new Map<string, typeof projected>();
  for (const entry of projected) {
    const tid = entry.version.templateId;
    if (!tid) continue;
    byTemplate.set(tid, [...(byTemplate.get(tid) ?? []), entry]);
  }

  const sets: LooseEndSet[] = [];
  for (const [tid, entries] of byTemplate) {
    if (entries.length < 2) continue;
    const bySignature = new Map<string, typeof entries>();
    for (const entry of entries) {
      const sig = defaultsSignature(entry.version);
      bySignature.set(sig, [...(bySignature.get(sig) ?? []), entry]);
    }
    let i = 0;
    for (const [, same] of bySignature) {
      if (same.length < 2) continue;
      sets.push({
        key: `${tid}:${i++}`,
        templateTitle: same[0].version.templateTitle,
        entries: [...same].sort((a, b) => b.version.usage - a.version.usage),
      });
    }
  }
  return sets.sort((a, b) => b.entries.length - a.entries.length);
};

// ----- Near matches (loose ends tier 2) -----------------------------------

interface Reconciled {
  durationLabel: string | null;
  modalityLabel: string | null;
  placeOfServiceLabel: string | null;
  placeOfServiceCode: string | null;
  cptCodes: string[];
  forms: TypeDefaults['forms'];
  reminders: TypeDefaults['reminders'];
}

const emptyReconciled = (): Reconciled => ({
  durationLabel: null,
  modalityLabel: null,
  placeOfServiceLabel: null,
  placeOfServiceCode: null,
  cptCodes: [],
  forms: [],
  reminders: [],
});

/**
 * Fold a version's defaults into the reconciliation. Returns null on a
 * genuine conflict (both sides set, different values) — unset-vs-set is
 * NOT a conflict; the merge adopts the set value. This is what separates
 * "Intake (60 min)" ~ "Psychiatric Intake (no duration)" (compatible)
 * from "Follow-up (20 min)" vs "Intake (40 min)" (legit variants, never
 * offered).
 */
const foldDefaults = (acc: Reconciled, d: TypeDefaults): Reconciled | null => {
  const pick = <T>(a: T | null, b: T | null, eq: (x: T, y: T) => boolean): T | null | 'CONFLICT' => {
    if (a === null || (Array.isArray(a) && a.length === 0)) return b;
    if (b === null || (Array.isArray(b) && b.length === 0)) return a;
    return eq(a, b) ? a : 'CONFLICT';
  };
  const scalarEq = (x: string, y: string): boolean => x === y;
  const jsonEq = <T>(x: T, y: T): boolean => JSON.stringify(x) === JSON.stringify(y);

  const duration = pick(acc.durationLabel, d.durationLabel, scalarEq);
  const modality = pick(acc.modalityLabel, d.modalityLabel, scalarEq);
  const pos = pick(acc.placeOfServiceCode, d.placeOfServiceCode, scalarEq);
  const cpt = pick(acc.cptCodes.length ? acc.cptCodes : null, d.cptCodes.length ? d.cptCodes : null, jsonEq);
  const forms = pick(acc.forms.length ? acc.forms : null, d.forms.length ? d.forms : null, jsonEq);
  const reminders = pick(acc.reminders.length ? acc.reminders : null, d.reminders.length ? d.reminders : null, jsonEq);
  if ([duration, modality, pos, cpt, forms, reminders].includes('CONFLICT')) {
    return null;
  }
  return {
    durationLabel: duration,
    modalityLabel: modality,
    placeOfServiceLabel:
      (pos) === acc.placeOfServiceCode
        ? acc.placeOfServiceLabel
        : d.placeOfServiceLabel,
    placeOfServiceCode: pos,
    cptCodes: (cpt as string[] | null) ?? [],
    forms: (forms as TypeDefaults['forms'] | null) ?? [],
    reminders: (reminders as TypeDefaults['reminders'] | null) ?? [],
  };
};

const tokens = (name: string): Set<string> =>
  new Set(name.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));

const isSubset = (a: Set<string>, b: Set<string>): boolean =>
  a.size > 0 && [...a].every((t) => b.has(t));

/**
 * Near-match sets (loose ends tier 2): same template, COMPATIBLE (but not
 * identical) defaults — the "Intake" ~ "Psychiatric Intake" gray zone the
 * exact tier deliberately skips. Rows already consumed by an exact set are
 * excluded so every row is owned by one decision. Compatibility isn't
 * transitive, so sets grow greedily (usage desc) and only while a single
 * reconciled outcome exists.
 */
export const computeNearMatchSets = (
  data: WizardData,
  resolutions: Readonly<Record<string, GroupResolution>>,
  exactSets: LooseEndSet[],
  /** COMMITTED exact-tier decisions — keepers survive into the near pool. */
  exactDecisions: Readonly<Record<string, LooseEndDecision>> = {}
): LooseEndSet[] => {
  // The near pool is the catalog AFTER the exact tier: merge losers and
  // archived sets are gone, but each merge's KEEPER remains (under its
  // kept name) — that's exactly where "Intake" meets "Psychiatric Intake".
  // Skipped exact sets keep their rows out: they already had their offer.
  const excluded = new Set<string>();
  const keeperNames = new Map<string, string>();
  for (const set of exactSets) {
    for (const e of set.entries) excluded.add(e.version.id);
    const decision = exactDecisions[set.key];
    if (decision?.merge && !decision.archive) {
      const keeper =
        set.entries.find((e) => e.name === decision.keepName)?.version ??
        set.entries[0].version;
      excluded.delete(keeper.id);
      keeperNames.set(keeper.id, decision.keepName);
    }
  }
  const pool = projectSharedCatalog(data, resolutions)
    .filter((e) => !excluded.has(e.version.id) && e.version.templateId)
    .map((e) =>
      keeperNames.has(e.version.id)
        ? { ...e, name: keeperNames.get(e.version.id) as string }
        : e
    );

  const byTemplate = new Map<string, typeof pool>();
  for (const entry of pool) {
    const tid = entry.version.templateId as string;
    byTemplate.set(tid, [...(byTemplate.get(tid) ?? []), entry]);
  }

  const sets: LooseEndSet[] = [];
  for (const [tid, rows] of byTemplate) {
    if (rows.length < 2) continue;
    const remaining = [...rows].sort((a, b) => b.version.usage - a.version.usage);
    let i = 0;
    while (remaining.length > 1) {
      const seed = remaining.shift() as (typeof rows)[number];
      let reconciled = foldDefaults(emptyReconciled(), seed.version.defaults);
      const members = [seed];
      for (const candidate of [...remaining]) {
        if (!reconciled) break;
        const next = foldDefaults(reconciled, candidate.version.defaults);
        if (next) {
          reconciled = next;
          members.push(candidate);
          remaining.splice(remaining.indexOf(candidate), 1);
        }
      }
      if (members.length >= 2 && reconciled) {
        const tokenSets = members.map((m) => tokens(m.name));
        const namesOverlap = tokenSets.some((a, x) =>
          tokenSets.some(
            (b, y) => x !== y && (isSubset(a, b) || isSubset(b, a))
          )
        );
        sets.push({
          key: `near:${tid}:${i++}`,
          templateTitle: members[0].version.templateTitle,
          entries: members,
          near: {
            reconciled: {
              templateName: members[0].version.templateTitle,
              templateSet: true,
              ...reconciled,
            },
            namesOverlap,
          },
        });
      }
    }
  }
  // Names-overlap sets first (highest confidence), then by size.
  return sets.sort(
    (a, b) =>
      Number(b.near?.namesOverlap ?? false) -
        Number(a.near?.namesOverlap ?? false) ||
      b.entries.length - a.entries.length
  );
};

/**
 * How many OTHER projected shared types use this template — the Step 2
 * education hint ("you'll get to consolidate these at the end").
 */
export const templateReuseCount = (
  data: WizardData,
  templateId: string | null,
  excludeVersionId: string
): number => {
  if (!templateId) return 0;
  return data.sharedTypes.filter(
    (v) => v.templateId === templateId && v.id !== excludeVersionId
  ).length;
};
